import * as React from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { StatusBadge } from "@/components/ui/status"
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalFooter } from "@/components/modal"
import { api } from "@/lib/api"
import { useNotificationStore } from "@/store"
import {
  GitBranch,
  Play,
  Pause,
  Trash2,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  ChevronRight,
  Zap,
} from "lucide-react"

function WorkflowDesigner() {
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)
  const [selectedWorkflow, setSelectedWorkflow] = React.useState(null)
  const [isExecuting, setIsExecuting] = React.useState(false)

  const { data: workflows, isLoading } = useQuery({
    queryKey: ["workflows"],
    queryFn: () => api.get("/workflows"),
  })

  const { data: runs } = useQuery({
    queryKey: ["workflow-runs"],
    queryFn: () => api.get("/workflows/runs"),
  })

  const executeMutation = useMutation({
    mutationFn: ({ id, input }) => api.post(`/workflows/${id}/execute`, { input }),
    onSuccess: () => {
      queryClient.invalidateQueries(["workflow-runs"])
      addNotification({ type: "success", message: "Workflow executed successfully" })
      setIsExecuting(false)
    },
    onError: (error) => {
      addNotification({ type: "error", message: error.message })
      setIsExecuting(false)
    },
  })

  const handleExecute = (workflow) => {
    setSelectedWorkflow(workflow)
    setIsExecuting(true)
    executeMutation.mutate({ id: workflow.id, input: {} })
  }

  const renderStep = (step, index, isLast) => {
    const stepIcons = {
      condition: "🔀",
      transform: "🔄",
      api_call: "🌐",
      database: "🗄️",
      notification: "🔔",
      delay: "⏰",
    }

    return (
      <div key={index} className="flex items-center">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-lg">
            {stepIcons[step.type] || "📋"}
          </div>
          <span className="text-xs text-muted-foreground mt-1">{step.name}</span>
        </div>
        {!isLast && (
          <ChevronRight className="h-6 w-6 text-muted-foreground mx-2" />
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Workflow Designer</h1>
          <p className="text-muted-foreground">Design and manage automated workflows</p>
        </div>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          New Workflow
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {workflows?.data?.map((workflow) => (
          <Card key={workflow.id} className="cursor-pointer hover:border-primary transition-colors">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <GitBranch className="h-5 w-5" />
                  {workflow.name}
                </CardTitle>
                <StatusBadge status={workflow.is_enabled ? "ok" : "offline"} />
              </div>
              <p className="text-sm text-muted-foreground">{workflow.description}</p>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Play className="h-4 w-4" />
                    {workflow.total_runs || 0} runs
                  </span>
                  <span className="flex items-center gap-1 text-success">
                    <CheckCircle className="h-4 w-4" />
                    {workflow.success_runs || 0}
                  </span>
                  <span className="flex items-center gap-1 text-destructive">
                    <XCircle className="h-4 w-4" />
                    {workflow.failed_runs || 0}
                  </span>
                </div>
              </div>

              {/* Visual Workflow */}
              <div className="flex items-center overflow-x-auto py-4 border rounded-lg bg-muted/30">
                {workflow.steps?.map((step, index) => (
                  renderStep(step, index, index === workflow.steps.length - 1)
                ))}
              </div>

              <div className="flex items-center justify-between mt-4">
                <Badge variant="outline">
                  <Clock className="mr-1 h-3 w-3" />
                  {workflow.trigger}
                </Badge>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleExecute(workflow)}
                    disabled={isExecuting}
                  >
                    <Zap className="mr-1 h-4 w-4" />
                    Run
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Runs */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Workflow Runs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {runs?.data?.slice(0, 10).map((run) => (
              <div key={run.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <StatusBadge status={run.status === "success" ? "ok" : run.status === "failed" ? "error" : "warning"} />
                  <div>
                    <p className="font-medium">{run.workflow_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(run.created_at * 1000).toLocaleString()}
                    </p>
                  </div>
                </div>
                {run.error && (
                  <p className="text-sm text-destructive">{run.error}</p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default WorkflowDesigner
